import * as Y from "yjs";
import type {
  Condition,
  FormContent,
  Question,
  QuestionConfig,
  QuestionOption,
  QuestionType,
  Section,
  SectionJump,
  ValidationRule,
} from "../types";
import { byPos, initialKeys, keyForDrop } from "../sortKey";
import { questionsInFormOrder } from "../logic";
import {
  canConditionOn,
  definitionFor,
  hasOptions,
  hasRows,
  takesAnswer,
} from "../registry";
import { newId } from "../ids";

/**
 * The collaborative draft.
 *
 *   Y.Doc
 *   ├── meta       Y.Map   title, description
 *   ├── sections   Y.Map   sectionId → Y.Map
 *   └── questions  Y.Map   questionId → Y.Map
 *
 * Two things are load-bearing here.
 *
 * Sections and questions are keyed by id and ordered by a `pos` sort key rather
 * than held in a Y.Array. A Y.Array has no move operation, so reordering one is
 * a delete plus an insert, and two admins dragging the same question at once
 * produce two copies of it that no later merge can reconcile. Writing one sort
 * key resolves to a single winner instead.
 *
 * Each question is its own Y.Map rather than a plain object, so "rename the
 * label" and "toggle required" merge instead of clobbering each other.
 */

export const META_KEY = "meta";
export const SECTIONS_KEY = "sections";
export const QUESTIONS_KEY = "questions";

type YMap = Y.Map<unknown>;

export function metaMap(doc: Y.Doc): YMap {
  return doc.getMap(META_KEY);
}
export function sectionsMap(doc: Y.Doc): Y.Map<YMap> {
  return doc.getMap(SECTIONS_KEY);
}
export function questionsMap(doc: Y.Doc): Y.Map<YMap> {
  return doc.getMap(QUESTIONS_KEY);
}

function readString(map: YMap, key: string): string | undefined {
  const value = map.get(key);
  return typeof value === "string" ? value : undefined;
}

/**
 * Read a question's settings, which are stored as their own map.
 *
 * Settings are per-key so two admins changing different ones — the options and
 * the scale bounds, say — merge instead of one silently replacing the other's
 * whole object. Older documents hold a plain object here; read those as they
 * are, and they convert on the next write.
 */
/** The two settings stored as identified maps of `QuestionOption` rows. */
const OPTION_LIST_KEYS = ["options", "rows"] as const;
type OptionListKey = (typeof OPTION_LIST_KEYS)[number];

function isOptionListKey(key: string): key is OptionListKey {
  return (OPTION_LIST_KEYS as readonly string[]).includes(key);
}

function readConfig(map: YMap): QuestionConfig | undefined {
  const value = map.get("config");
  if (value instanceof Y.Map) {
    const config: Record<string, unknown> = {};
    for (const [key, entry] of value.entries()) {
      config[key] = isOptionListKey(key) ? readOptions(entry) : entry;
    }
    for (const key of OPTION_LIST_KEYS) {
      if (config[key] === undefined) delete config[key];
    }
    return Object.keys(config).length ? (config as QuestionConfig) : undefined;
  }
  const legacy = readJson<Record<string, unknown>>(map, "config");
  if (!legacy) return undefined;
  return {
    ...legacy,
    ...(legacy.options === undefined
      ? {}
      : { options: readOptions(legacy.options) }),
  } as QuestionConfig;
}

/** Read a question's options. */
function readOptions(value: unknown): QuestionOption[] | undefined {
  if (value instanceof Y.Map) {
    const options: QuestionOption[] = [];
    for (const [id, entry] of value.entries()) {
      if (!(entry instanceof Y.Map)) continue;
      const label = entry.get("label");
      const pos = entry.get("pos");
      options.push({
        id,
        label: typeof label === "string" ? label : "",
        pos: typeof pos === "string" ? pos : "",
      });
    }
    // An empty map means the question has no options, not that it has a list
    // of none: the map is kept attached even for types that don't use it.
    return options.length ? options.sort(byPos) : undefined;
  }
  return undefined;
}

/**
 * The options a freshly created question of this type starts with.
 *
 * Ids are minted per question rather than declared in the registry, so no two
 * questions ever share one — an answer names a question and an option, and
 * shared ids would make that pair ambiguous the moment anything reads across
 * questions.
 */
function seedOptions(type: QuestionType): QuestionOption[] | undefined {
  return mintedList(definitionFor(type).defaultOptionLabels);
}

/** The rows a freshly created grid starts with — same minting as options. */
function seedRows(type: QuestionType): QuestionOption[] | undefined {
  return mintedList(definitionFor(type).defaultRowLabels);
}

function mintedList(
  labels: readonly string[] | undefined,
): QuestionOption[] | undefined {
  if (!labels) return undefined;
  const keys = initialKeys(labels.length);
  return labels.map((label, index) => ({
    id: newId("opt"),
    label,
    pos: keys[index] ?? "",
  }));
}

/** A question's settings map, converting a legacy plain object in place. */
function ensureConfigMap(entry: YMap): Y.Map<unknown> {
  const existing = entry.get("config");
  if (existing instanceof Y.Map) return existing as Y.Map<unknown>;
  const legacy = (readJson<Record<string, unknown>>(entry, "config") ??
    {}) as Record<string, unknown>;
  const map = new Y.Map<unknown>();
  entry.set("config", map);
  for (const [key, value] of Object.entries(legacy)) {
    if (isOptionListKey(key)) writeOptions(map, readOptions(value), key);
    else map.set(key, value);
  }
  return map;
}

/** An option-list map (options or rows), created with the question if absent. */
function ensureOptionsMap(
  config: Y.Map<unknown>,
  key: OptionListKey = "options",
): Y.Map<unknown> {
  const existing = config.get(key);
  if (existing instanceof Y.Map) return existing as Y.Map<unknown>;
  // An options list this build doesn't recognise. There is no such draft —
  // options have been identified maps since before any form was authored —
  // but replacing one would throw the old options away without telling
  // anybody, and every condition naming them with it. If that assumption is
  // ever wrong, this is how we find out rather than how we lose the data.
  if (Array.isArray(existing) && existing.length > 0) {
    throw new Error(
      "This form's options were written in a shape this version can't read.",
    );
  }
  const map = new Y.Map<unknown>();
  config.set(key, map);
  return map;
}

/** One option is its own map, so two admins renaming different ones merge. */
function writeOption(options: Y.Map<unknown>, option: QuestionOption): void {
  let entry = options.get(option.id);
  if (!(entry instanceof Y.Map)) {
    entry = new Y.Map();
    options.set(option.id, entry);
  }
  const map = entry as Y.Map<unknown>;
  if (map.get("label") !== option.label) map.set("label", option.label);
  if (map.get("pos") !== option.pos) map.set("pos", option.pos);
}

/**
 * Replace the whole option list, dropping any the new one no longer has.
 *
 * Returns what it dropped, because an option that disappears takes every
 * condition naming it with it — and this is the one place options are removed
 * in bulk, so reporting it here is what stops a future caller reintroducing a
 * dangling reference by going through a different door.
 */
function writeOptions(
  config: Y.Map<unknown>,
  options: QuestionOption[] | undefined,
  key: OptionListKey = "options",
): Set<string> {
  const map = ensureOptionsMap(config, key);
  const next = new Map((options ?? []).map((option) => [option.id, option]));
  const removed = new Set<string>();
  // eslint-disable-next-line unicorn/no-useless-spread -- snapshot: the loop deletes from `map`
  for (const key of [...map.keys()]) {
    if (next.has(key)) continue;
    map.delete(key);
    removed.add(key);
  }
  for (const option of next.values()) writeOption(map, option);
  return removed;
}

/**
 * Read a question's validation rules, stored as a map keyed by rule kind.
 *
 * Same reason as settings: adding a maximum length while someone else adds an
 * email check must not discard one of them. A question holds at most one rule
 * of each kind, so the kind is a natural key.
 */
function readValidation(map: YMap): ValidationRule[] | undefined {
  const value = map.get("validation");
  if (value instanceof Y.Map) {
    const rules = [...value.values()] as ValidationRule[];
    return rules.length ? rules : undefined;
  }
  return readJson<ValidationRule[]>(map, "validation");
}

/**
 * Write settings key by key, dropping any the new value no longer has.
 *
 * Returns the option ids it dropped, so the caller can clear what named them.
 */
function writeConfig(
  entry: YMap,
  config: QuestionConfig | undefined,
): Set<string> {
  const map = ensureConfigMap(entry);
  const next = (config ?? {}) as Record<string, unknown>;
  // Keys the new value doesn't carry are cleared, which for options means
  // losing all of them at once.
  const cleared: Array<[string, unknown]> = [...map.keys()]
    .filter((key) => next[key] === undefined)
    .map((key) => [key, undefined]);
  return applyConfigKeys(map, [...cleared, ...Object.entries(next)]);
}

/**
 * Write settings onto a question's config map, reporting any options that
 * disappeared.
 *
 * The single place config keys are written, because there are two ways to
 * lose an option — replacing the list with a shorter one, and clearing it
 * outright — and a second implementation only ever remembers one of them. An
 * option that vanishes without its conditions being cleared leaves a test
 * nothing can satisfy, which hides the question it guards with no way to get
 * it back.
 */
function applyConfigKeys(
  map: Y.Map<unknown>,
  entries: Iterable<[string, unknown]>,
): Set<string> {
  const removed = new Set<string>();
  for (const [key, value] of entries) {
    if (!isOptionListKey(key)) {
      if (value === undefined) map.delete(key);
      else map.set(key, value);
      continue;
    }
    if (value === undefined) {
      // Emptied, never deleted. Re-creating this map is the one write two
      // admins can't merge — both make a different one at the same key, Yjs
      // keeps one, and everything written into the other is discarded. So it
      // stays attached even to a question with no use for it.
      const options = ensureOptionsMap(map, key);
      // eslint-disable-next-line unicorn/no-useless-spread -- snapshot: the loop deletes from `options`
      for (const id of [...options.keys()]) {
        removed.add(id);
        options.delete(id);
      }
    } else {
      for (const id of writeOptions(map, value as QuestionOption[], key)) {
        removed.add(id);
      }
    }
  }
  return removed;
}

/** The rules map, converting a legacy array in place and keeping its contents. */
function ensureValidationMap(entry: YMap): Y.Map<unknown> {
  const target = entry.get("validation");
  if (target instanceof Y.Map) return target as Y.Map<unknown>;
  const existing = readJson<ValidationRule[]>(entry, "validation") ?? [];
  const map = new Y.Map<unknown>();
  entry.set("validation", map);
  for (const rule of existing) map.set(rule.kind, rule);
  return map;
}

/** Write rules keyed by kind, dropping any the new list no longer has. */
function writeValidation(
  entry: YMap,
  rules: ValidationRule[] | undefined,
): void {
  let target = entry.get("validation");
  if (!(target instanceof Y.Map)) {
    target = new Y.Map();
    entry.set("validation", target);
  }
  const map = target as Y.Map<unknown>;
  const next = new Map((rules ?? []).map((rule) => [rule.kind, rule]));
  // eslint-disable-next-line unicorn/no-useless-spread -- snapshot: the loop deletes from `map`
  for (const key of [...map.keys()]) {
    if (!next.has(key as ValidationRule["kind"])) map.delete(key);
  }
  for (const [kind, rule] of next) map.set(kind, rule);
}

function readJson<T>(map: YMap, key: string): T | undefined {
  const value = map.get(key);
  return value === undefined || value === null ? undefined : (value as T);
}

/**
 * Read a field where `null` and "absent" mean different things.
 *
 * `defaultNextSectionId` is the case: `null` submits the form here, while an
 * omitted value falls through to the next section. Collapsing them — as the
 * plain reader above does — silently turns "submit here" into "keep going",
 * exposing a whole section nobody meant to ask.
 */
function readNullable<T>(map: YMap, key: string): T | null | undefined {
  if (!map.has(key)) return undefined;
  const value = map.get(key);
  return value === null ? null : (value as T);
}

/**
 * Read the whole draft out as plain data.
 *
 * Tolerant on purpose: a half-written question from another tab must not throw
 * mid-render, so anything unreadable falls back to a sane default and the UI
 * shows it rather than crashing.
 */
export function readForm(doc: Y.Doc): FormContent {
  const meta = metaMap(doc);
  const sections: Section[] = [];
  for (const [id, map] of sectionsMap(doc).entries()) {
    sections.push({
      id,
      title: readString(map, "title") ?? "",
      description: readString(map, "description"),
      pos: readString(map, "pos") ?? "",
      jumps: readJson<SectionJump[]>(map, "jumps"),
      defaultNextSectionId: readNullable<string>(map, "defaultNextSectionId"),
      visibleWhen: readJson<Condition>(map, "visibleWhen"),
    });
  }

  const questions: Question[] = [];
  for (const [id, map] of questionsMap(doc).entries()) {
    questions.push({
      id,
      sectionId: readString(map, "sectionId") ?? "",
      type: (readString(map, "type") ?? "short_answer") as QuestionType,
      label: readString(map, "label") ?? "",
      shortDescription: readString(map, "shortDescription"),
      help: readString(map, "help"),
      required: map.get("required") === true,
      pos: readString(map, "pos") ?? "",
      config: readConfig(map),
      validation: readValidation(map),
      visibleWhen: readJson<Condition>(map, "visibleWhen"),
    });
  }

  const content = {
    title: readString(meta, "title") ?? "",
    description: readString(meta, "description"),
    sections: sections.sort(byPos),
    questions,
  };
  // Document order — sections by key, then questions within each. Sorting the
  // whole list by `pos` would interleave sections, since the key only orders
  // questions inside one.
  return { ...content, questions: questionsInFormOrder(content) };
}

/**
 * Create the nested maps every question is expected to carry.
 *
 * Questions written before a map existed don't have it — `rows` postdates the
 * first drafts, and the oldest configs were plain objects — and the maps are
 * otherwise created lazily by whichever client first writes. Two clients doing
 * that concurrently each make a different map at the same key; Yjs keeps one,
 * and everything the losing admin put in theirs is discarded. The server runs
 * this once when it loads a draft, before any client can write, so every
 * client only ever writes into maps the document already shares.
 *
 * Idempotent, and a no-op on a document that already carries the maps — no
 * update is emitted, so running it on every load costs nothing.
 */
export function ensureQuestionMaps(doc: Y.Doc, origin?: unknown): void {
  doc.transact(() => {
    for (const [, entry] of questionsMap(doc).entries()) {
      const config = ensureConfigMap(entry);
      for (const key of OPTION_LIST_KEYS) ensureOptionsMap(config, key);
      if (!(entry.get("validation") instanceof Y.Map)) {
        ensureValidationMap(entry);
      }
    }
  }, origin);
}

/** Seed an empty document. Safe to call on an already-seeded one. */
export function initializeForm(
  doc: Y.Doc,
  content: Pick<FormContent, "title"> & Partial<FormContent>,
  origin?: unknown,
): void {
  doc.transact(() => {
    const meta = metaMap(doc);
    if (meta.get("title") === undefined) meta.set("title", content.title);
    if (
      content.description !== undefined &&
      meta.get("description") === undefined
    ) {
      meta.set("description", content.description);
    }
    for (const section of content.sections ?? []) upsertSectionIn(doc, section);
    for (const question of content.questions ?? [])
      upsertQuestionIn(doc, question);
  }, origin);
}

function upsertSectionIn(doc: Y.Doc, section: Section): void {
  const map = sectionsMap(doc);
  let entry = map.get(section.id);
  if (!entry) {
    entry = new Y.Map();
    map.set(section.id, entry);
  }
  entry.set("title", section.title);
  entry.set("pos", section.pos);
  if (section.description !== undefined)
    entry.set("description", section.description);
  if (section.jumps !== undefined) entry.set("jumps", section.jumps);
  if (section.defaultNextSectionId !== undefined) {
    // Stored as an explicit null, not skipped: null means "submit here".
    entry.set("defaultNextSectionId", section.defaultNextSectionId);
  }
  if (section.visibleWhen !== undefined)
    entry.set("visibleWhen", section.visibleWhen);
}

function upsertQuestionIn(doc: Y.Doc, question: Question): void {
  const map = questionsMap(doc);
  let entry = map.get(question.id);
  if (!entry) {
    entry = new Y.Map();
    map.set(question.id, entry);
  }
  entry.set("sectionId", question.sectionId);
  entry.set("type", question.type);
  entry.set("label", question.label);
  entry.set("required", question.required);
  entry.set("pos", question.pos);
  // Create the nested maps up front. Two clients that each create one at the
  // same key concurrently produce two maps, and the merge keeps one — taking
  // whatever the loser had put inside it. Creating them with the question means
  // both sides only ever write keys into a map they already share.
  if (!(entry.get("config") instanceof Y.Map)) writeConfig(entry, undefined);
  // Including the option-list maps, and for every type: a question that
  // becomes a choice question or a grid later must find these maps already
  // here rather than two admins each creating one.
  ensureOptionsMap(ensureConfigMap(entry), "options");
  ensureOptionsMap(ensureConfigMap(entry), "rows");
  if (!(entry.get("validation") instanceof Y.Map)) {
    ensureValidationMap(entry);
  }
  if (question.shortDescription !== undefined)
    entry.set("shortDescription", question.shortDescription);
  if (question.help !== undefined) entry.set("help", question.help);
  if (question.config !== undefined) writeConfig(entry, question.config);
  if (question.validation !== undefined)
    writeValidation(entry, question.validation);
  if (question.visibleWhen !== undefined)
    entry.set("visibleWhen", question.visibleWhen);
}

/**
 * Every write the builder can make.
 *
 * Each one is a single transaction so it lands as one update on the wire and
 * one step in undo. Callers never touch the Y types directly — that keeps the
 * document's shape in one file rather than spread across the UI.
 */
function clonedConditionRewriter(sources: Question[], copies: Question[]) {
  const mapped = new Map(
    sources.map((source, index) => [source.id, copies[index]!]),
  );
  return (condition: Condition | undefined): Condition | undefined => {
    if (!condition) return undefined;
    const copy = mapped.get(condition.questionId);
    if (!copy) return { ...condition };
    const source = sources.find((q) => q.id === condition.questionId)!;
    const optionIndex =
      source.config?.options?.findIndex((o) => o.id === condition.value) ?? -1;
    return {
      ...condition,
      questionId: copy.id,
      ...(optionIndex >= 0
        ? { value: copy.config?.options?.[optionIndex]?.id }
        : {}),
    };
  };
}

function cloneQuestions(sources: Question[], sectionId: string): Question[] {
  const positions = initialKeys(sources.length);
  const copies = sources.map((source, index) => ({
    ...structuredClone(source),
    id: newId("q"),
    // New IDs must not change the order of siblings with tied legacy keys.
    pos: positions[index]!,
    sectionId,
    ...(source.config
      ? {
          config: {
            ...structuredClone(source.config),
            ...(source.config.options
              ? {
                  options: mintedList(
                    source.config.options.map((o) => o.label),
                  ),
                }
              : {}),
            ...(source.config.rows
              ? {
                  rows: mintedList(source.config.rows.map((o) => o.label)),
                }
              : {}),
          },
        }
      : {}),
  }));
  const rewrite = clonedConditionRewriter(sources, copies);
  return copies.map((copy) => ({
    ...copy,
    visibleWhen: rewrite(copy.visibleWhen),
  }));
}

export class FormDraft {
  constructor(
    readonly doc: Y.Doc,
    private readonly origin: unknown = "local",
  ) {}

  read(): FormContent {
    return readForm(this.doc);
  }

  transact(fn: () => void): void {
    this.doc.transact(fn, this.origin);
  }

  /** Copies keep external dependencies and remap references within the selection. */
  copyQuestions(
    ids: readonly string[],
    sectionId: string,
    afterId?: string,
  ): string[] {
    const form = this.read();
    if (!form.sections.some((section) => section.id === sectionId)) return [];
    const selected = questionsInFormOrder(form).filter((q) =>
      ids.includes(q.id),
    );
    const copies = cloneQuestions(selected, sectionId);
    const siblings = form.questions
      .filter((q) => q.sectionId === sectionId)
      .sort(byPos);
    let previousId = afterId ?? siblings.at(-1)?.id;
    this.transact(() => {
      for (const copy of copies) {
        const index = previousId
          ? siblings.findIndex((q) => q.id === previousId) + 1
          : 0;
        const positioned = {
          ...copy,
          pos: keyForDrop(siblings, copy.id, index),
        };
        upsertQuestionIn(this.doc, positioned);
        siblings.push(positioned);
        siblings.sort(byPos);
        previousId = copy.id;
      }
    });
    return copies.map((q) => q.id);
  }

  duplicateSection(id: string): string | null {
    const form = this.read();
    const index = form.sections.findIndex((section) => section.id === id);
    const source = form.sections[index];
    if (!source) return null;
    const newSectionId = newId("sec");
    const questions = form.questions
      .filter((q) => q.sectionId === id)
      .sort(byPos);
    const copies = cloneQuestions(questions, newSectionId);
    const rewrite = clonedConditionRewriter(questions, copies);
    this.transact(() => {
      upsertSectionIn(this.doc, {
        ...source,
        id: newSectionId,
        title: `${source.title || "Untitled section"} (copy)`,
        pos: keyForDrop(form.sections, newSectionId, index + 1),
        visibleWhen: rewrite(source.visibleWhen),
        jumps: source.jumps?.map((jump) => ({
          ...jump,
          when: rewrite(jump.when)!,
        })),
      });
      for (const copy of copies) upsertQuestionIn(this.doc, copy);
    });
    return newSectionId;
  }

  setTitle(title: string): void {
    this.transact(() => metaMap(this.doc).set("title", title));
  }

  setDescription(description: string): void {
    this.transact(() => metaMap(this.doc).set("description", description));
  }

  addSection(id: string, title: string): void {
    const sections = this.read().sections;
    const pos = keyForDrop(sections, id, sections.length);
    this.transact(() => upsertSectionIn(this.doc, { id, title, pos }));
  }

  updateSection(id: string, patch: Partial<Omit<Section, "id">>): void {
    this.transact(() => {
      const entry = sectionsMap(this.doc).get(id);
      if (!entry) return;
      for (const [key, value] of Object.entries(patch)) {
        if (value === undefined) entry.delete(key);
        else entry.set(key, value);
      }
    });
  }

  /**
   * Remove a section, its questions, and every reference to it.
   *
   * References matter as much as the questions: a jump pointing at a section
   * that no longer exists blocks publishing, and an explicit next pointing at
   * one silently changes where applicants go.
   */
  removeSection(id: string): void {
    this.transact(() => {
      sectionsMap(this.doc).delete(id);
      const questions = questionsMap(this.doc);
      const removed = new Set<string>();
      // eslint-disable-next-line unicorn/no-useless-spread -- snapshot: the loop deletes from `questions`
      for (const [questionId, entry] of [...questions.entries()]) {
        if (entry.get("sectionId") !== id) continue;
        questions.delete(questionId);
        removed.add(questionId);
      }
      // Anything elsewhere that depended on those questions must be cleared,
      // exactly as it is when a question is deleted on its own.
      this.clearReferencesTo(removed);
      for (const [, entry] of sectionsMap(this.doc).entries()) {
        const jumps = entry.get("jumps") as SectionJump[] | undefined;
        if (jumps?.some((jump) => jump.toSectionId === id)) {
          entry.set(
            "jumps",
            jumps.filter((jump) => jump.toSectionId !== id),
          );
        }
        // Fall through to the next section rather than pointing at nothing.
        if (entry.get("defaultNextSectionId") === id) {
          entry.delete("defaultNextSectionId");
        }
      }
    });
  }

  /**
   * Re-home questions whose section is gone.
   *
   * A section can be deleted while another admin is offline adding a question
   * to it; that question merges in afterwards and belongs nowhere. It blocks
   * publishing and the builder can't show it, so the draft would need repairing
   * by hand. Moving orphans into the first section makes them visible, and the
   * admin can then move or delete them.
   */
  adoptOrphanedQuestions(): void {
    const form = this.read();
    const first = form.sections[0];
    if (!first) return;
    const sectionIds = new Set(form.sections.map((section) => section.id));
    const orphans = form.questions.filter(
      (question) => !sectionIds.has(question.sectionId),
    );
    if (orphans.length === 0) return;
    this.transact(() => {
      for (const orphan of orphans) {
        const entry = questionsMap(this.doc).get(orphan.id);
        entry?.set("sectionId", first.id);
      }
    });
  }

  addQuestion(
    id: string,
    sectionId: string,
    type: QuestionType = "short_answer",
    label = "",
  ): void {
    const form = this.read();
    const siblings = form.questions.filter((q) => q.sectionId === sectionId);
    const pos = keyForDrop(siblings, id, siblings.length);
    this.transact(() =>
      upsertQuestionIn(this.doc, {
        id,
        sectionId,
        type,
        label,
        required: false,
        pos,
        config: {
          ...definitionFor(type).defaultConfig,
          options: seedOptions(type),
          rows: seedRows(type),
        },
      }),
    );
  }

  updateQuestion(id: string, patch: Partial<Omit<Question, "id">>): void {
    this.transact(() => {
      const entry = questionsMap(this.doc).get(id);
      if (!entry) return;
      for (const [key, value] of Object.entries(patch)) {
        // Settings and rules are their own maps so concurrent edits to
        // different ones merge; writing the whole object here would put back
        // the clobbering this exists to avoid.
        if (key === "config") {
          const removed = writeConfig(
            entry,
            value as QuestionConfig | undefined,
          );
          if (removed.size > 0) this.clearConditionsOnOptions(id, removed);
        } else if (key === "validation") {
          writeValidation(entry, value as ValidationRule[] | undefined);
        } else if (value === undefined) {
          entry.delete(key);
        } else {
          entry.set(key, value);
        }
      }
    });
  }

  /**
   * Change one setting without touching the others.
   *
   * Prefer this to passing a whole `config` through `updateQuestion`: that
   * replaces the object, so a concurrent edit to a *different* setting is
   * discarded. Pass `undefined` for a value to clear that setting.
   */
  setQuestionConfig(id: string, partial: Partial<QuestionConfig>): void {
    this.transact(() => {
      const entry = questionsMap(this.doc).get(id);
      if (!entry) return;
      const removed = applyConfigKeys(
        ensureConfigMap(entry),
        Object.entries(partial),
      );
      if (removed.size > 0) this.clearConditionsOnOptions(id, removed);
      // Turning the Other box off strands any condition watching it — same
      // silent failure as a deleted option, cleared the same way.
      if ("allowOther" in partial && partial.allowOther !== true) {
        this.clearOtherConditionsOn(id);
      }
    });
  }

  /**
   * Add an option to a choice question.
   *
   * Options are minted with an id because a stored answer points at one: an
   * admin fixing a typo in a label must not orphan everybody who already
   * picked it.
   */
  addOption(questionId: string, label = ""): string {
    return this.addListEntry(questionId, "options", label);
  }

  /** Add a row to a grid — options and rows share one storage shape. */
  addRow(questionId: string, label = ""): string {
    return this.addListEntry(questionId, "rows", label);
  }

  private addListEntry(
    questionId: string,
    key: OptionListKey,
    label: string,
  ): string {
    const question = this.read().questions.find((q) => q.id === questionId);
    if (!question) return "";
    const existing = question.config?.[key] ?? [];
    const id = newId("opt");
    const pos = keyForDrop([...existing, { id, pos: "" }], id, existing.length);
    this.transact(() => {
      const entry = questionsMap(this.doc).get(questionId);
      if (!entry) return;
      const config = ensureConfigMap(entry);
      writeOption(ensureOptionsMap(config, key), { id, label, pos });
    });
    return id;
  }

  /**
   * Rename one option, without touching the others.
   *
   * One key write, so two admins editing different options merge — which is
   * why the builder edits options as a row each rather than as one text box.
   */
  setOptionLabel(questionId: string, optionId: string, label: string): void {
    this.setListEntryLabel(questionId, "options", optionId, label);
  }

  setRowLabel(questionId: string, rowId: string, label: string): void {
    this.setListEntryLabel(questionId, "rows", rowId, label);
  }

  private setListEntryLabel(
    questionId: string,
    key: OptionListKey,
    entryId: string,
    label: string,
  ): void {
    this.transact(() => {
      const entry = questionsMap(this.doc).get(questionId);
      if (!entry) return;
      const options = ensureOptionsMap(ensureConfigMap(entry), key);
      const option = options.get(entryId);
      if (option instanceof Y.Map) option.set("label", label);
    });
  }

  /**
   * Remove an option, and with it anything that depended on it.
   *
   * A condition pointing at a deleted option can never fire again, which
   * silently hides whatever it guarded — the same failure a deleted question
   * causes, so it gets the same treatment.
   */
  removeOption(questionId: string, optionId: string): void {
    this.transact(() => {
      const entry = questionsMap(this.doc).get(questionId);
      if (!entry) return;
      const options = ensureOptionsMap(ensureConfigMap(entry));
      options.delete(optionId);
      this.clearConditionsOnOptions(questionId, new Set([optionId]));
    });
  }

  /**
   * Remove a grid row. No conditions to clear: a condition can't name a row —
   * grids aren't condition sources at all.
   */
  removeRow(questionId: string, rowId: string): void {
    this.transact(() => {
      const entry = questionsMap(this.doc).get(questionId);
      if (!entry) return;
      ensureOptionsMap(ensureConfigMap(entry), "rows").delete(rowId);
    });
  }

  /** Move an option within its question — one sort-key write, as with a drag. */
  moveOption(questionId: string, optionId: string, toIndex: number): void {
    this.moveListEntry(questionId, "options", optionId, toIndex);
  }

  moveRow(questionId: string, rowId: string, toIndex: number): void {
    this.moveListEntry(questionId, "rows", rowId, toIndex);
  }

  private moveListEntry(
    questionId: string,
    key: OptionListKey,
    entryId: string,
    toIndex: number,
  ): void {
    const question = this.read().questions.find((q) => q.id === questionId);
    const options = question?.config?.[key];
    if (!options?.some((option) => option.id === entryId)) return;
    const pos = keyForDrop([...options].sort(byPos), entryId, toIndex);
    this.transact(() => {
      const entry = questionsMap(this.doc).get(questionId);
      if (!entry) return;
      const map = ensureOptionsMap(ensureConfigMap(entry), key);
      const option = map.get(entryId);
      if (option instanceof Y.Map) option.set("pos", pos);
    });
  }

  /**
   * Add or replace one validation rule, leaving the rest alone.
   *
   * A question holds at most one rule of each kind, so the kind is the key —
   * and two admins adding *different* rules at once both survive, where
   * replacing the whole list would drop one.
   */
  setValidationRule(id: string, rule: ValidationRule): void {
    this.transact(() => {
      const entry = questionsMap(this.doc).get(id);
      if (!entry) return;
      const map = ensureValidationMap(entry);
      map.set(rule.kind, rule);
    });
  }

  removeValidationRule(id: string, kind: ValidationRule["kind"]): void {
    this.transact(() => {
      const entry = questionsMap(this.doc).get(id);
      if (!entry) return;
      ensureValidationMap(entry).delete(kind);
    });
  }

  /**
   * Change a question's type, resetting the settings that no longer apply.
   *
   * Validation rules the new type doesn't offer are dropped rather than left
   * invisibly attached — a maxLength on a date question would silently reject
   * answers with no way to see why.
   */
  changeQuestionType(id: string, type: QuestionType): void {
    const question = this.read().questions.find((q) => q.id === id);
    if (!question) return;
    const definition = definitionFor(type);
    const kept = (question.validation ?? []).filter((rule) =>
      definition.validators.includes(rule.kind),
    );
    // Keep only the settings the new type actually uses. Spreading the old
    // config over the defaults left `options` attached to a paragraph or date
    // question — invisible to the applicant but still shown in the builder,
    // and still compared by the version diff.
    const carried: QuestionConfig = {};
    for (const key of Object.keys(definition.defaultConfig) as Array<
      keyof QuestionConfig
    >) {
      const value = question.config?.[key];
      if (value !== undefined) {
        (carried as Record<string, unknown>)[key] = value;
      }
    }
    // Options survive a move between choice types, ids and all: switching a
    // question from multiple choice to checkboxes asks the same thing a
    // different way, and reminting the ids would orphan every answer and every
    // condition pointing at them. The same ids carry into and out of a grid,
    // where they are its columns. Moving to a type without options drops
    // them, and moving back seeds fresh ones.
    const options = hasOptions(type)
      ? (question.config?.options ?? seedOptions(type))
      : undefined;
    const rows = hasRows(type)
      ? (question.config?.rows ?? seedRows(type))
      : undefined;
    const config = { ...definition.defaultConfig, ...carried, options, rows };

    // One transaction for the whole change, so no collaborator ever sees the
    // half of it. Leaving the choice family drops every option this question
    // had, and `updateQuestion` clears the conditions naming them as part of
    // the same write; a condition left behind holds an opaque id nothing can
    // match, which publishing does not refuse and which silently hides the
    // question it guards.
    this.transact(() => {
      this.updateQuestion(id, {
        type,
        config,
        validation: kept.length ? kept : undefined,
        // A type with nothing to answer can't require an answer. The builder
        // hides the switch for it, so a stranded flag would be unfixable.
        ...(takesAnswer(type) ? {} : { required: false }),
      });
      if (!canConditionOn(type)) {
        // A grid or text block can't steer anything, so every condition that
        // read this question is now a test nothing can pass — the same silent
        // hiding a deleted question causes, cleared the same way.
        this.clearReferencesTo(new Set([id]));
        return;
      }
      // Losing the "Other" box takes the conditions watching it with it, for
      // the same reason a deleted option takes its conditions.
      if (config.allowOther !== true) this.clearOtherConditionsOn(id);
      this.normalizeConditionsOn(id, type);
    });
  }

  /**
   * Point every condition on this question at the right operator for its type.
   *
   * "Equals" on a checkbox group means exactly one box is ticked, so a
   * dependent disappears the moment a second is ticked; "contains" on a single
   * choice is a substring test that can match the wrong option. Conditions are
   * created with the right operator, but a later type change would otherwise
   * leave every existing one pointing the wrong way.
   */
  private normalizeConditionsOn(sourceId: string, type: QuestionType): void {
    const wantedOp = type === "checkboxes" ? "contains" : "equals";
    this.rewriteConditions((condition) => {
      if (condition.questionId !== sourceId) return condition;

      // An "other" test stays an "other" test — it reads which kind of choice
      // was made, not a value, so only its operator can go stale. Conditions
      // stranded by the Other box disappearing are cleared before this runs.
      const wantedCompare =
        condition.compare === "other"
          ? "other"
          : hasOptions(type)
            ? "option"
            : "text";

      // Presence tests read no value, so only the comparison mode can be
      // stale for them.
      const comparing =
        condition.op === "equals" || condition.op === "contains";
      const op = comparing ? wantedOp : condition.op;
      if (op === condition.op && condition.compare === wantedCompare) {
        return condition;
      }
      // What the value *means* has to move with the type as well as which
      // operator applies. A dependent left marked as comparing text while its
      // source became a choice question ends up holding an option id compared
      // against the applicant's typed answer: it matches nothing, publishing
      // accepts it, and the question it guards never appears.
      return { ...condition, op, compare: wantedCompare };
    });
  }

  /**
   * Clear every reference to questions that no longer exist.
   *
   * A condition pointing at a deleted question never fires, which silently
   * hides whatever depended on it — and publishing refuses the draft outright.
   * Both removal paths go through here, because deleting a section deletes its
   * questions too and left exactly the same dangling references behind.
   */
  private clearReferencesTo(ids: ReadonlySet<string>): void {
    this.rewriteConditions((condition) =>
      ids.has(condition.questionId) ? null : condition,
    );
  }

  /**
   * Clear every condition that compared against an option that is now gone.
   *
   * On a choice question a condition holds an option id, so a deleted option
   * leaves a test that can never pass — the dependent question disappears with
   * no way to bring it back, which is exactly the silent failure a dangling
   * question reference causes.
   *
   * Both ways of losing an option come through here: deleting one, and
   * changing the question to a type that has no options at all.
   */
  private clearConditionsOnOptions(
    questionId: string,
    optionIds: ReadonlySet<string>,
  ): void {
    this.rewriteConditions((condition) =>
      condition.questionId === questionId &&
      condition.value !== undefined &&
      optionIds.has(condition.value)
        ? null
        : condition,
    );
  }

  /**
   * Clear every condition watching this question's "Other" box.
   *
   * Fires when the box goes away — the type changes to one without it, or the
   * setting is switched off. A condition left watching a box nobody can take
   * never fires, silently hiding what it guards.
   */
  private clearOtherConditionsOn(questionId: string): void {
    this.rewriteConditions((condition) =>
      condition.questionId === questionId && condition.compare === "other"
        ? null
        : condition,
    );
  }

  /** Visit every stored condition in one transaction. Return the original
   * condition to leave it untouched, a replacement to update it, or null to
   * remove it. Removing a jump's condition removes the entire jump. */
  private rewriteConditions(
    rewrite: (condition: Condition) => Condition | null,
  ): void {
    const rewriteVisibility = (entry: YMap) => {
      const condition = entry.get("visibleWhen") as Condition | undefined;
      if (condition === undefined) return;
      const next = rewrite(condition);
      if (next === condition) return;
      if (next === null) entry.delete("visibleWhen");
      else entry.set("visibleWhen", next);
    };

    this.transact(() => {
      for (const entry of questionsMap(this.doc).values())
        rewriteVisibility(entry);
      for (const entry of sectionsMap(this.doc).values()) {
        rewriteVisibility(entry);
        const jumps = entry.get("jumps") as SectionJump[] | undefined;
        if (!jumps?.length) continue;
        let changed = false;
        const updated: SectionJump[] = [];
        for (const jump of jumps) {
          const when = rewrite(jump.when);
          if (when === jump.when) updated.push(jump);
          else {
            changed = true;
            if (when !== null) updated.push({ ...jump, when });
          }
        }
        if (changed) entry.set("jumps", updated);
      }
    });
  }

  removeQuestion(id: string): void {
    this.transact(() => {
      questionsMap(this.doc).delete(id);
      this.clearReferencesTo(new Set([id]));
    });
  }

  /**
   * Move a question to `toIndex` within `sectionId`, where the index is into
   * that section's questions as the dragger currently sees them.
   */
  moveQuestion(id: string, sectionId: string, toIndex: number): void {
    const form = this.read();
    const moved = form.questions.find((q) => q.id === id);
    if (!moved) return;
    // The target section can be deleted by someone else between the drag
    // starting and the drop landing. Moving into it would leave the question
    // pointing at nothing — invisible in the builder and dropped by every
    // reader — so keep it where it is.
    if (!form.sections.some((section) => section.id === sectionId)) return;
    const siblings = form.questions
      .filter((q) => q.sectionId === sectionId)
      .sort(byPos);
    // Dragging into a different section: the question isn't among the siblings
    // yet, so add it before working out where the drop lands.
    const ordered = siblings.some((q) => q.id === id)
      ? siblings
      : [...siblings, { ...moved, sectionId }];
    this.updateQuestion(id, {
      pos: keyForDrop(ordered, id, toIndex),
      sectionId,
    });
  }

  moveSection(id: string, toIndex: number): void {
    const pos = keyForDrop(this.read().sections, id, toIndex);
    this.updateSection(id, { pos });
  }
}
